import React, { useEffect, useState, Children } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
function Counter({
  from,
  to,
  duration = 2,
  suffix = '%'
}: {
  from: number;
  to: number;
  duration?: number;
  suffix?: string;
}) {
  const [count, setCount] = useState(from);
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.5
  });
  useEffect(() => {
    if (inView) {
      let startTimestamp: number;
      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
        setCount(Math.floor(progress * (to - from) + from));
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [inView, from, to, duration]);
  return <span ref={ref} data-id="element-201">
      {count}
      {suffix}
    </span>;
}
export function WhyThisWins() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  const universities = ['Harvard', 'Stanford', 'Wharton', 'INSEAD', 'LBS', 'Oxford', 'Cambridge'];
  return <section id="how-it-works" className="py-24 md:py-32 bg-primary relative" data-id="element-202">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-203">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-20" data-id="element-204">
          <h2 className="text-xs text-accent tracking-[0.2em] uppercase mb-4" data-id="element-205">
            The Advantage
          </h2>
          <h3 className="text-4xl md:text-5xl font-serif text-text-light" data-id="element-206">
            Why This Wins
          </h3>
          <div className="w-24 h-[1px] bg-accent mx-auto mt-8" data-id="element-207" />
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-208">
          {/* Block 1 */}
          <motion.div variants={itemVariants} className="bg-secondary border border-border p-8 md:p-10 flex flex-col justify-between md:col-span-2 lg:col-span-1" data-id="element-209">
            <div data-id="element-210">
              <span className="text-accent font-serif text-2xl mb-4 block" data-id="element-211">
                01
              </span>
              <h4 className="text-2xl font-serif text-text-light mb-4" data-id="element-212">
                Top 1% Talent — Without Legacy Overhead
              </h4>
              <p className="text-text-muted mb-8" data-id="element-213">
                You get future Fortune 500 leaders, not recycled thinking. We
                source exclusively from elite institutions.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-214">
              {universities.map(uni => <span key={uni} className="text-xs uppercase tracking-widest text-text-light/70 border border-border px-3 py-1.5" data-id="element-215">
                  {uni}
                </span>)}
            </div>
          </motion.div>

          {/* Block 2 */}
          <motion.div variants={itemVariants} className="bg-secondary border border-border p-8 md:p-10" data-id="element-216">
            <span className="text-accent font-serif text-2xl mb-4 block" data-id="element-217">
              02
            </span>
            <h4 className="text-2xl font-serif text-text-light mb-4" data-id="element-218">
              Better Thinking, Not Just More Slides
            </h4>
            <p className="text-text-muted mb-6" data-id="element-219">
              Traditional firms optimise for process. We optimise for insight
              and outcome.
            </p>
            <ul className="space-y-3 text-sm text-text-light/80" data-id="element-220">
              <li className="flex items-center before:content-['—'] before:text-accent before:mr-3" data-id="element-221">
                Fresh, unconstrained thinking
              </li>
              <li className="flex items-center before:content-['—'] before:text-accent before:mr-3" data-id="element-222">
                Cutting-edge frameworks and data approaches
              </li>
              <li className="flex items-center before:content-['—'] before:text-accent before:mr-3" data-id="element-223">
                Zero political bias or internal agenda
              </li>
            </ul>
          </motion.div>

          {/* Block 3 - The Big Stat */}
          <motion.div variants={itemVariants} className="bg-secondary border border-border p-8 md:p-10 flex flex-col justify-center items-center text-center md:col-span-2" data-id="element-224">
            <span className="text-accent font-serif text-2xl mb-2 block self-start md:self-center" data-id="element-225">
              03
            </span>
            <div className="text-6xl md:text-8xl font-serif text-gradient-navy mb-6" data-id="element-226">
              <Counter from={0} to={70} duration={1.5} suffix="" data-id="element-227" />–
              <Counter from={0} to={90} duration={2} data-id="element-228" />
            </div>
            <h4 className="text-2xl font-serif text-text-light mb-4" data-id="element-229">
              Cost Advantage
            </h4>
            <p className="text-text-muted max-w-2xl" data-id="element-230">
              No partner margins. No bloated teams. No unnecessary overhead.
              Just high-impact strategy work—delivered efficiently.
            </p>
          </motion.div>

          {/* Block 4 */}
          <motion.div variants={itemVariants} className="bg-secondary border border-border p-8 md:p-10" data-id="element-231">
            <span className="text-accent font-serif text-2xl mb-4 block" data-id="element-232">
              04
            </span>
            <h4 className="text-2xl font-serif text-text-light mb-4" data-id="element-233">
              Built for Speed & Focus
            </h4>
            <div className="space-y-4 mt-6" data-id="element-234">
              <div className="flex items-start" data-id="element-235">
                <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 mr-4 shrink-0" data-id="element-236" />
                <p className="text-text-muted text-sm" data-id="element-237">Define the problem</p>
              </div>
              <div className="flex items-start" data-id="element-238">
                <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 mr-4 shrink-0" data-id="element-239" />
                <p className="text-text-muted text-sm" data-id="element-240">
                  Get matched with a specialist team
                </p>
              </div>
              <div className="flex items-start" data-id="element-241">
                <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 mr-4 shrink-0" data-id="element-242" />
                <p className="text-text-muted text-sm" data-id="element-243">
                  Receive structured, actionable outputs in weeks—not months
                </p>
              </div>
            </div>
          </motion.div>

          {/* Block 5 */}
          <motion.div variants={itemVariants} className="bg-secondary border border-border p-8 md:p-10" data-id="element-244">
            <span className="text-accent font-serif text-2xl mb-4 block" data-id="element-245">
              05
            </span>
            <h4 className="text-2xl font-serif text-text-light mb-4" data-id="element-246">
              Built-In Talent Pipeline
            </h4>
            <p className="text-text-muted mb-6" data-id="element-247">
              Every project doubles as a live assessment centre. You're not just
              buying strategy—you're:
            </p>
            <ul className="space-y-3 text-sm text-text-light/80" data-id="element-248">
              <li className="flex items-center before:content-['—'] before:text-accent before:mr-3" data-id="element-249">
                Identifying future hires
              </li>
              <li className="flex items-center before:content-['—'] before:text-accent before:mr-3" data-id="element-250">
                Building relationships with top-tier talent early
              </li>
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}